import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

export const KyuStore3DFactory: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // Timing helper
  const sp = (delay: number, damping = 14, stiffness = 110) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 0.9 },
    });

  // Camera Dolly & Subtle Orbit
  const camRotateY = interpolate(frame, [0, durationInFrames], [-6, 6], {
    extrapolateRight: "clamp",
  });
  const camRotateX = interpolate(frame, [0, 60, durationInFrames], [8, 4, 2], {
    extrapolateRight: "clamp",
  });
  const camZoom = interpolate(frame, [0, durationInFrames], [1, 1.05], {
    extrapolateRight: "clamp",
  });

  // Conveyor Belt Animation (infinite loop offset)
  const conveyorOffset = (frame * 6) % 120;

  // Avatar Walking bobbing
  const avatarBob = Math.sin(frame * 0.35) * 8;
  const avatarTilt = Math.sin(frame * 0.18) * 2;

  // Bot Floating gentle motion
  const bot1Y = Math.sin(frame * 0.12) * 10;
  const bot2Y = Math.sin(frame * 0.15 + 1.2) * 12;

  // Terminal Typing logic
  const fullCommand = "> /auto-inject --id=8492019 --zone=2104";
  const typedChars = Math.min(
    fullCommand.length,
    Math.floor(interpolate(frame, [50, 140], [0, fullCommand.length], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }))
  );
  const cursorBlink = Math.floor(frame / 12) % 2 === 0 ? "|" : " ";

  // Subtitle synchronization
  const SUBTITLES = [
    { from: 0, to: 95, text: "Pernah mikir gak, gimana diamond Kyu Store bisa masuk secepat ini?" },
    { from: 95, to: 190, text: "Begitu lu submit User ID, tiga bot otomatis langsung bergerak." },
    { from: 190, to: 280, text: "Verifikasi nama akun, cek transaksi QRIS, dan inject diamond." },
    { from: 280, to: 380, text: "Selesai dalam nol koma delapan detik. Sat-set, aman, anti-minus!" },
    { from: 380, to: 450, text: "Top-up game legal sekarang di kyustore.com!" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0B0F19",
        overflow: "hidden",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Audio Layer */}
      <Audio src={staticFile("kyustore_3d_factory_vo.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.10} />
      {frame === 10 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.6} />}
      {frame === 60 && <Audio src={staticFile("click.mp3")} volume={0.5} />}
      {frame === 180 && <Audio src={staticFile("ui-success.mp3")} volume={0.7} />}
      {frame === 280 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}

      {/* 3D WORLD PERSPECTIVE STAGE */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          perspective: "1400px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 1080,
            height: 1920,
            transformStyle: "preserve-3d",
            transform: `scale(${camZoom}) rotateX(${camRotateX}deg) rotateY(${camRotateY}deg)`,
            transition: "transform 0.1s ease-out",
          }}
        >
          {/* Ambient Lighting & Glow Backdrop */}
          <div
            style={{
              position: "absolute",
              top: "20%",
              left: "15%",
              width: 760,
              height: 760,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(16, 185, 129, 0.18) 0%, rgba(93, 63, 211, 0.12) 50%, transparent 70%)",
              filter: "blur(60px)",
              pointerEvents: "none",
            }}
          />

          {/* 3D Grid Floor with Perspective Reflection */}
          <div
            style={{
              position: "absolute",
              bottom: "-100px",
              left: "-200px",
              width: 1480,
              height: 1100,
              background: `
                radial-gradient(ellipse at center, rgba(15, 23, 42, 0.8) 0%, #0B0F19 80%),
                linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)
              `,
              backgroundSize: "100% 100%, 60px 60px, 60px 60px",
              transform: "rotateX(75deg) translateZ(-80px)",
              boxShadow: "inset 0 0 100px #0B0F19",
            }}
          />

          {/* BACKGROUND NEON SIGNS */}
          {/* Sign 1: Glowing Green "VERIFIKASI" */}
          <div
            style={{
              position: "absolute",
              top: 480,
              left: 140,
              transform: "translateZ(-150px) rotateY(12deg)",
              padding: "16px 36px",
              borderRadius: 16,
              background: "rgba(16, 185, 129, 0.08)",
              border: "3px solid #10B981",
              boxShadow: "0 0 35px rgba(16, 185, 129, 0.4), inset 0 0 20px rgba(16, 185, 129, 0.2)",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: "#10B981",
                boxShadow: "0 0 12px #10B981",
              }}
            />
            <span
              style={{
                fontSize: 32,
                fontWeight: 900,
                letterSpacing: "4px",
                color: "#A7F3D0",
                textShadow: "0 0 15px #10B981, 0 0 30px #10B981",
              }}
            >
              VERIFIKASI ID
            </span>
          </div>

          {/* Sign 2: Glowing Warm Orange "KYU STORE HQ" */}
          <div
            style={{
              position: "absolute",
              top: 490,
              right: 140,
              transform: "translateZ(-120px) rotateY(-10deg)",
              padding: "14px 30px",
              borderRadius: 16,
              background: "rgba(255, 176, 32, 0.08)",
              border: "3px solid #FFB020",
              boxShadow: "0 0 35px rgba(255, 176, 32, 0.35)",
            }}
          >
            <span
              style={{
                fontSize: 28,
                fontWeight: 900,
                letterSpacing: "3px",
                color: "#FDE68A",
                textShadow: "0 0 12px #FFB020, 0 0 24px #FFB020",
              }}
            >
              SERVER KYU
            </span>
          </div>

          {/* CONVEYOR BELT (Ban Berjalan Transaksi) */}
          <div
            style={{
              position: "absolute",
              top: 1040,
              left: "50%",
              width: 820,
              height: 130,
              transform: "translateX(-50%) rotateX(60deg) translateZ(-40px)",
              background: "#1E293B",
              borderTop: "4px solid #475569",
              borderBottom: "4px solid #475569",
              boxShadow: "0 25px 50px rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            {/* Conveyor rollers / track pattern */}
            <div
              style={{
                width: "100%",
                height: "100%",
                backgroundImage: "repeating-linear-gradient(90deg, #334155 0px, #334155 20px, #1E293B 20px, #1E293B 40px)",
                backgroundPositionX: `${conveyorOffset}px`,
              }}
            />
          </div>

          {/* CUTE BOT MASCOT 1 (Left Side - "Bot 1: Cek ID") */}
          <div
            style={{
              position: "absolute",
              top: 760 + bot1Y,
              left: 120,
              transform: "translateZ(80px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Floating Tag Card */}
            <div
              style={{
                background: "#FFFFFF",
                padding: "8px 18px",
                borderRadius: 12,
                boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
                fontSize: 18,
                fontWeight: 800,
                color: "#0F172A",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ color: "#10B981" }}>●</span> Bot 1 / Cek ID
            </div>

            {/* Voxel Box Body (Stylized Clay Cube) */}
            <div
              style={{
                width: 110,
                height: 100,
                borderRadius: 24,
                background: "linear-gradient(145deg, #FDA4AF, #FB7185)",
                boxShadow: "0 15px 30px rgba(251, 113, 133, 0.4), inset 0 2px 4px rgba(255,255,255,0.6)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {/* Cute Digital Eyes */}
              <div style={{ display: "flex", gap: 24 }}>
                <div style={{ width: 14, height: 20, borderRadius: 6, background: "#1E293B" }} />
                <div style={{ width: 14, height: 20, borderRadius: 6, background: "#1E293B" }} />
              </div>
              {/* Little antenna */}
              <div
                style={{
                  position: "absolute",
                  top: -14,
                  width: 8,
                  height: 14,
                  background: "#94A3B8",
                  borderRadius: "4px 4px 0 0",
                }}
              />
            </div>
          </div>

          {/* CUTE BOT MASCOT 2 (Right Side - "Bot 2: Auto-QRIS") */}
          <div
            style={{
              position: "absolute",
              top: 780 + bot2Y,
              right: 120,
              transform: "translateZ(60px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Floating Tag Card */}
            <div
              style={{
                background: "#FFFFFF",
                padding: "8px 18px",
                borderRadius: 12,
                boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
                fontSize: 18,
                fontWeight: 800,
                color: "#0F172A",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ color: "#5D3FD3" }}>●</span> Bot 2 / QRIS
            </div>

            {/* Voxel Box Body */}
            <div
              style={{
                width: 105,
                height: 95,
                borderRadius: 22,
                background: "linear-gradient(145deg, #FDE68A, #F59E0B)",
                boxShadow: "0 15px 30px rgba(245, 158, 11, 0.4), inset 0 2px 4px rgba(255,255,255,0.6)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <div style={{ display: "flex", gap: 20 }}>
                <div style={{ width: 14, height: 18, borderRadius: 6, background: "#1E293B" }} />
                <div style={{ width: 14, height: 18, borderRadius: 6, background: "#1E293B" }} />
              </div>
              <div
                style={{
                  position: "absolute",
                  top: -12,
                  width: 8,
                  height: 12,
                  background: "#94A3B8",
                  borderRadius: "4px 4px 0 0",
                }}
              />
            </div>
          </div>

          {/* MAIN CHIBI HERO AVATAR (Center Stage Strutting Forward) */}
          <div
            style={{
              position: "absolute",
              top: 680 + avatarBob,
              left: "50%",
              transform: `translateX(-50%) translateZ(160px) rotateZ(${avatarTilt}deg)`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              zIndex: 30,
            }}
          >
            {/* 3D Soft Shadow under avatar */}
            <div
              style={{
                position: "absolute",
                bottom: -40,
                width: 220,
                height: 40,
                borderRadius: "50%",
                background: "rgba(0, 0, 0, 0.55)",
                filter: "blur(12px)",
                transform: "scale(" + (1 - Math.abs(avatarBob) * 0.02) + ")",
              }}
            />

            {/* Chibi Head Container */}
            <div style={{ position: "relative", width: 180, height: 180, marginBottom: -10 }}>
              {/* Spiky Anime Hair / Beanie Back */}
              <div
                style={{
                  position: "absolute",
                  width: 170,
                  height: 160,
                  borderRadius: "50% 50% 45% 45%",
                  background: "#1E1B4B",
                  top: 0,
                  left: 5,
                }}
              />
              {/* Spotted Beret / Cap (Law-style iconic look) */}
              <div
                style={{
                  position: "absolute",
                  width: 150,
                  height: 80,
                  borderRadius: "40px 40px 20px 20px",
                  background: "#F1F5F9",
                  top: -10,
                  left: 15,
                  boxShadow: "0 6px 15px rgba(0,0,0,0.25)",
                  backgroundImage: "radial-gradient(#1E293B 22%, transparent 22%)",
                  backgroundSize: "28px 28px",
                }}
              />
              {/* Face Skin */}
              <div
                style={{
                  position: "absolute",
                  width: 140,
                  height: 110,
                  borderRadius: "0 0 70px 70px",
                  background: "linear-gradient(to bottom, #FED7AA, #FDBA74)",
                  top: 55,
                  left: 20,
                  boxShadow: "inset 0 -5px 10px rgba(0,0,0,0.1)",
                }}
              />
              {/* Cool Dark Sunglasses */}
              <div
                style={{
                  position: "absolute",
                  top: 75,
                  left: 25,
                  display: "flex",
                  gap: 12,
                  zIndex: 10,
                }}
              >
                <div
                  style={{
                    width: 58,
                    height: 38,
                    borderRadius: 10,
                    background: "#0F172A",
                    border: "3px solid #334155",
                    boxShadow: "0 6px 12px rgba(0,0,0,0.4)",
                  }}
                />
                <div
                  style={{
                    width: 58,
                    height: 38,
                    borderRadius: 10,
                    background: "#0F172A",
                    border: "3px solid #334155",
                    boxShadow: "0 6px 12px rgba(0,0,0,0.4)",
                  }}
                />
              </div>
              {/* Confident Smirk */}
              <div
                style={{
                  position: "absolute",
                  bottom: 22,
                  left: 75,
                  width: 28,
                  height: 8,
                  borderRadius: "0 0 10px 10px",
                  borderBottom: "3px solid #78350F",
                }}
              />
            </div>

            {/* Chibi Body (Dark Hoodie Jacket) */}
            <div
              style={{
                width: 150,
                height: 160,
                borderRadius: "32px 32px 20px 20px",
                background: "linear-gradient(to bottom, #1E1B4B, #0F172A)",
                boxShadow: "0 15px 35px rgba(0,0,0,0.45)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: 15,
                position: "relative",
              }}
            >
              {/* Kyu Store Gold Logo Badge on Chest */}
              <div
                style={{
                  padding: "4px 12px",
                  borderRadius: 8,
                  background: "#FFB020",
                  fontSize: 12,
                  fontWeight: 900,
                  color: "#0F172A",
                  letterSpacing: "1px",
                }}
              >
                KYU VIP
              </div>
            </div>

            {/* Chunky Stylized Sneakers */}
            <div style={{ display: "flex", gap: 32, marginTop: -15 }}>
              <div
                style={{
                  width: 55,
                  height: 34,
                  borderRadius: "14px 22px 10px 10px",
                  background: "#F8FAFC",
                  borderBottom: "6px solid #5D3FD3",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
                }}
              />
              <div
                style={{
                  width: 55,
                  height: 34,
                  borderRadius: "22px 14px 10px 10px",
                  background: "#F8FAFC",
                  borderBottom: "6px solid #5D3FD3",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
                }}
              />
            </div>
          </div>

          {/* FLOATING TERMINAL PROMPT BOX (Detik 2-8) */}
          <div
            style={{
              position: "absolute",
              top: 1080,
              left: "50%",
              transform: "translateX(-50%) translateZ(220px)",
              width: 740,
              padding: "16px 28px",
              borderRadius: 16,
              background: "rgba(15, 23, 42, 0.88)",
              border: "2px solid rgba(255, 255, 255, 0.12)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              gap: 16,
              zIndex: 40,
            }}
          >
            {/* Terminal macOS traffic lights */}
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#EF4444" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#F59E0B" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#10B981" }} />
            </div>
            {/* Command Text */}
            <span
              style={{
                fontFamily: "monospace",
                fontSize: 22,
                color: "#38BDF8",
                fontWeight: 700,
                letterSpacing: "0.5px",
              }}
            >
              {fullCommand.slice(0, typedChars)}
              <span style={{ color: "#38BDF8" }}>{cursorBlink}</span>
            </span>
          </div>

          {/* FLOATING METRIC BADGE (0.8s Rata-rata Masuk) */}
          <div
            style={{
              position: "absolute",
              top: 1220,
              right: 140,
              transform: `translateZ(240px) scale(${sp(180)})`,
              background: "rgba(255, 255, 255, 0.95)",
              padding: "18px 28px",
              borderRadius: 20,
              boxShadow: "0 20px 45px rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              gap: 18,
              zIndex: 45,
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 16,
                background: "#ECFDF5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
              }}
            >
              ⚡
            </div>
            <div>
              <div style={{ fontSize: 36, fontWeight: 900, color: "#059669", lineHeight: 1 }}>0.8s</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#64748B", marginTop: 4 }}>
                Otomatis Masuk
              </div>
            </div>
          </div>

          {/* FLOATING CHECKLIST CARD (Kiri Bawah) */}
          <div
            style={{
              position: "absolute",
              top: 1220,
              left: 140,
              transform: `translateZ(240px) scale(${sp(200)})`,
              background: "rgba(255, 255, 255, 0.95)",
              padding: "18px 26px",
              borderRadius: 20,
              boxShadow: "0 20px 45px rgba(0,0,0,0.4)",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              zIndex: 45,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
              <span style={{ color: "#10B981" }}>✔</span> Cek Nickname Otomatis
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
              <span style={{ color: "#10B981" }}>✔</span> 100% Anti-Minus Moonton
            </div>
          </div>
        </div>
      </div>

      {/* 2D VINTAGE MASKING TAPE HEADER (Top Banner) */}
      <div
        style={{
          position: "absolute",
          top: 180,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 100,
        }}
      >
        <div
          style={{
            position: "relative",
            background: "#FEF3C7",
            padding: "16px 42px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
            border: "1px dashed rgba(180, 83, 9, 0.3)",
          }}
        >
          {/* Semi-transparent Scotch Tape Left Corner */}
          <div
            style={{
              position: "absolute",
              top: -14,
              left: -20,
              width: 60,
              height: 28,
              background: "rgba(255, 255, 255, 0.65)",
              transform: "rotate(-18deg)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          />
          {/* Semi-transparent Scotch Tape Right Corner */}
          <div
            style={{
              position: "absolute",
              top: -14,
              right: -20,
              width: 60,
              height: 28,
              background: "rgba(255, 255, 255, 0.65)",
              transform: "rotate(16deg)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          />

          <span
            style={{
              fontSize: 26,
              fontWeight: 900,
              color: "#78350F",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
            }}
          >
            PABRIK TOP-UP: SISTEM 0.8 DETIK
          </span>
        </div>
      </div>

      {/* DYNAMIC KRAFT-PAPER SUBTITLES (Safe Zone Bottom: 400px) */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 390,
            left: "50%",
            transform: "translateX(-50%)",
            width: 860,
            display: "flex",
            justifyContent: "center",
            zIndex: 100,
          }}
        >
          <div
            style={{
              background: "#F8FAFC",
              padding: "16px 32px",
              borderRadius: 16,
              boxShadow: "0 15px 35px rgba(0,0,0,0.5)",
              border: "2px solid #5D3FD3",
              textAlign: "center",
            }}
          >
            <span
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: "#0F172A",
                lineHeight: 1.4,
              }}
            >
              {currentSub.text}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
